import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface InspectionPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  vehicleId: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
}

export default function InspectionPhotosModal({
  isOpen,
  onClose,
  bookingId,
  vehicleId,
  onUpdate,
  onError
}: InspectionPhotosModalProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [photos, setPhotos] = useState<Array<{ url: string; id: string }>>([]);

  useEffect(() => {
    if (isOpen && bookingId && vehicleId) {
      loadPhotos();
    }
  }, [isOpen, bookingId, vehicleId]);

  const loadPhotos = async () => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('inspection-photos')
        .list(`${bookingId}/${vehicleId}`);

      if (error) throw error;

      const photoUrls = await Promise.all(data.map(async (file) => {
        const { data: signedUrl } = await supabase.storage
          .from('inspection-photos')
          .createSignedUrl(`${bookingId}/${vehicleId}/${file.name}`, 60 * 60); // 1 hour expiry

        return {
          url: signedUrl?.signedUrl || '',
          id: `${bookingId}/${vehicleId}/${file.name}`
        };
      }));

      setPhotos(photoUrls.filter(photo => photo.url));
    } catch (err) {
      console.error('Error loading photos:', err);
      onError(err instanceof Error ? err.message : 'Failed to load photos');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !supabase) return;

    try {
      setIsUploading(true);
      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${bookingId}/${vehicleId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('inspection-photos')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: signedUrl } = await supabase.storage
          .from('inspection-photos')
          .createSignedUrl(filePath, 60 * 60); // 1 hour expiry

        return {
          url: signedUrl?.signedUrl || '',
          id: filePath
        };
      });

      const uploadedPhotos = await Promise.all(uploadPromises);
      setPhotos(prev => [...prev, ...uploadedPhotos.filter(photo => photo.url)]);
      await onUpdate();
    } catch (err) {
      console.error('Error uploading photos:', err);
      onError(err instanceof Error ? err.message : 'Failed to upload photos');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase.storage
        .from('inspection-photos')
        .remove([photoId]);

      if (error) throw error;

      setPhotos(prev => prev.filter(photo => photo.id !== photoId));
      await onUpdate();
    } catch (err) {
      console.error('Error deleting photo:', err);
      onError(err instanceof Error ? err.message : 'Failed to delete photo');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Inspection Photos</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4">
            <label className="block w-full">
              <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                <div className="flex flex-col items-center justify-center">
                  <Upload className="h-8 w-8 text-gray-400" />
                  <span className="mt-2 text-sm text-gray-600">
                    {isUploading ? 'Uploading...' : 'Click to upload photos'}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={handleFileUpload}
                    disabled={isUploading}
                  />
                </div>
              </div>
            </label>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.url}
                  alt="Inspection photo"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  onClick={() => handleDeletePhoto(photo.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
} 